import React, { useState, useEffect } from "react";
import { Box, Container, Typography, Card, Tabs, Tab } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SecurityIcon from "@mui/icons-material/Security";
import SettingsIcon from "@mui/icons-material/Settings";
import color from "../../components/utils/Colors";
import { useSnackbar } from "../../components/utils/SnackBar";
import AccountTab from "./AccountTab";
import NotificationTab from "./NotificationTab";
import PrivacyTab from "./PrivacyTab";
import PreferenceTab from "./PreferenceTab";
import PasswordPopup from "./PasswordPopup";
import DeleteAccountPopup from "./DeleteAccountPopup";
import {
  getProfile,
  updateProfile,
  getNotificationSettings,
  createNotificationSettings,
  updateNotificationSettings,
} from "../../services/services";
import { useUser } from "../../services/Context/UserContext";

export interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Settings: React.FC = () => {
  const showSnackbar = useSnackbar();
  const { user } = useUser();
  const [currentTab, setCurrentTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  // ── Account state ──────────────────────────────────────────────────────────
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");

  // ── Notification settings state ────────────────────────────────────────────
  const [notifSettingsId, setNotifSettingsId] = useState<number | null>(null);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [courseUpdates, setCourseUpdates] = useState(true);
  const [promotionalEmails, setPromotionalEmails] = useState(false);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [newMessages, setNewMessages] = useState(true);

  // ── Privacy state ──────────────────────────────────────────────────────────
  const [profileVisibility, setProfileVisibility] = useState("public");
  const [showActivity, setShowActivity] = useState(true);
  const [allowMessages, setAllowMessages] = useState(true);
  const [dataCollection, setDataCollection] = useState(true);

  // ── Preferences state ──────────────────────────────────────────────────────
  const [language, setLanguage] = useState("en");
  const [theme, setTheme] = useState("light");
  const [fontSize, setFontSize] = useState("medium");

  // ── Load user profile ──────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Pre-fill from UserContext immediately
    if (user) {
      setEmail(user.email ?? "");
      setUsername(user.name ?? "");
    }

    // Fetch full profile for phone and extra fields
    getProfile()
      .then((res: any) => {
        const data = res?.data?.data ?? res?.data ?? {};
        setEmail(data.email ?? user?.email ?? "");
        setUsername(data.name ?? user?.name ?? "");
        setPhone(data.phone ?? data.phoneNumber ?? "");
      })
      .catch(() => {
        // Silently use context values if profile fetch fails
      });
  }, [user?.id]);

  // ── Load notification settings ─────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.id) return;
    getNotificationSettings({ userId: user.id })
      .then((res: any) => {
        const data: any[] = res?.data?.data ?? [];
        if (data.length > 0) {
          const s = data[0];
          setNotifSettingsId(s.id);
          // Map API booleans to local UI state
          setCourseUpdates(
            !!s.isTrackNewLession || !!s.isTrackCourseCompleteness,
          );
          setEmailNotifications(
            !!(
              s.isTrackNewCourse ||
              s.isTrackNewLession ||
              s.isTrackSkillEffect
            ),
          );
        }
      })
      .catch(() => {
        // Use defaults if fetch fails
      });
  }, [user?.id]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSaveAccountSettings = async (values: {
    email: string;
    username: string;
    phone: string;
  }) => {
    try {
      await updateProfile({
        email: values.email,
        name: values.username,
        phone: values.phone,
      });
      setEmail(values.email);
      setUsername(values.username);
      setPhone(values.phone);
      showSnackbar("Account settings updated successfully");
    } catch {
      showSnackbar("Failed to update account settings");
    }
  };

  const handleSaveNotificationSettings = async (values: {
    emailNotifications: boolean;
    pushNotifications: boolean;
    courseUpdates: boolean;
    promotionalEmails: boolean;
    weeklyDigest: boolean;
    newMessages: boolean;
  }) => {
    try {
      const payload = {
        userId: user?.id,
        isTrackNewCourse: values.emailNotifications,
        isTrackNewLession: values.courseUpdates,
        isTrackCourseCompleteness: values.weeklyDigest,
        isTrackSkillEffect: values.newMessages,
        isTrackProfileCompleteness: values.pushNotifications,
        isTrackCourseVisibility: values.promotionalEmails,
      };

      if (notifSettingsId) {
        await updateNotificationSettings(notifSettingsId, payload);
      } else {
        const res: any = await createNotificationSettings(payload);
        if (res?.data?.data?.id) setNotifSettingsId(res.data.data.id);
      }

      setEmailNotifications(values.emailNotifications);
      setPushNotifications(values.pushNotifications);
      setCourseUpdates(values.courseUpdates);
      setPromotionalEmails(values.promotionalEmails);
      setWeeklyDigest(values.weeklyDigest);
      setNewMessages(values.newMessages);
      showSnackbar("Notification settings updated");
    } catch {
      showSnackbar("Failed to update notification settings");
    }
  };

  const handleSavePrivacySettings = (values: {
    profileVisibility: string;
    showActivity: boolean;
    allowMessages: boolean;
    dataCollection: boolean;
  }) => {
    setProfileVisibility(values.profileVisibility);
    setShowActivity(values.showActivity);
    setAllowMessages(values.allowMessages);
    setDataCollection(values.dataCollection);
    showSnackbar("Privacy settings updated");
  };

  const handleSavePreferences = (values: {
    language: string;
    theme: string;
    fontSize: string;
  }) => {
    setLanguage(values.language);
    setTheme(values.theme);
    setFontSize(values.fontSize);
    showSnackbar("Preferences updated");
  };

  const handleDeleteAccount = () => {
    setDeleteDialogOpen(false);
    showSnackbar(
      "Account deletion requested. Check your email for confirmation.",
    );
  };

  return (
    <Box
      sx={{ minHeight: "100vh", background: "transparent", paddingY: "40px" }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ marginBottom: "40px" }}>
          <Typography
            variant="h4"
            sx={{
              color: color.textColor,
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            Settings
          </Typography>
          <Typography variant="body1" sx={{ color: "#666" }}>
            Manage your account, privacy, and preferences
          </Typography>
        </Box>

        {/* Tabs container */}
        <Card sx={{ boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)" }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: `2px solid ${color.secondColor}`,
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "500",
                color: "#999",
                "&.Mui-selected": {
                  color: color.firstColor,
                  fontWeight: "bold",
                },
              },
            }}
          >
            <Tab
              label="Account"
              icon={<SettingsIcon />}
              iconPosition="start"
              id="settings-tab-0"
            />
            <Tab
              label="Notifications"
              icon={<NotificationsIcon />}
              iconPosition="start"
              id="settings-tab-1"
            />
            <Tab
              label="Privacy"
              icon={<SecurityIcon />}
              iconPosition="start"
              id="settings-tab-2"
            />
            <Tab
              label="Preferences"
              icon={<SettingsIcon />}
              iconPosition="start"
              id="settings-tab-3"
            />
          </Tabs>
          <AccountTab
            currentTab={currentTab}
            initialValues={{ email, username, phone }}
            onSave={handleSaveAccountSettings}
            onOpenPasswordDialog={() => setPasswordDialogOpen(true)}
            onOpenDeleteDialog={() => setDeleteDialogOpen(true)}
          />

          <NotificationTab
            currentTab={currentTab}
            initialValues={{
              emailNotifications,
              pushNotifications,
              courseUpdates,
              promotionalEmails,
              weeklyDigest,
              newMessages,
            }}
            onSave={handleSaveNotificationSettings}
          />

          <PrivacyTab
            currentTab={currentTab}
            initialValues={{
              profileVisibility,
              showActivity,
              allowMessages,
              dataCollection,
            }}
            onSave={handleSavePrivacySettings}
          />

          <PreferenceTab
            currentTab={currentTab}
            initialValues={{ language, theme, fontSize }}
            onSave={handleSavePreferences}
          />
        </Card>
      </Container>

      {/* Dialogs — rendered outside the Card so z-index stacks correctly */}
      {passwordDialogOpen && (
        <PasswordPopup
          open={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          onSuccess={() => showSnackbar("Password changed successfully")}
        />
      )}

      {deleteDialogOpen && (
        <DeleteAccountPopup
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteAccount}
        />
      )}
    </Box>
  );
};

export default Settings;
